import { useMemo, useState } from 'react'

import { SecondaryActionItem } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Upload } from '@factorialco/f0-react/icons/app'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Modals, ModalsProps } from 'modules/integrations/components/SyncButton/Modals'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export type UseSyncActionProps = {
  onConfirmSync?: ModalsProps['onConfirm']
  confirmTrackingEvent?: string
}

type UseSyncActionReturn = {
  dataCollectionSyncAction: SecondaryActionItem | null
  syncModal: React.ReactNode | null
}

export const useSyncAction = ({
  onConfirmSync,
  confirmTrackingEvent,
}: UseSyncActionProps = {}): UseSyncActionReturn => {
  const {
    capabilityMode,
    syncEnabled: canShowSyncAction,
    marketplaceIntegration,
    isSyncRunning,
  } = usePrivateIntegrationsSyncContext()
  const [showModal, setShowModal] = useState(false)

  const isExportMode = capabilityMode === 'file'

  const dataCollectionSyncAction = useMemo<SecondaryActionItem | null>(() => {
    if (!canShowSyncAction || !marketplaceIntegration) {
      return null
    }

    return {
      label: isExportMode
        ? i18n.t('compensations.integrations.export_action', {
            integration_name: marketplaceIntegration.name,
          })
        : i18n.t('compensations.integrations.sync_action', {
            integration_name: marketplaceIntegration.name,
          }),
      icon: isExportMode ? Upload : ArrowCycle,
      loading: isSyncRunning,
      onClick: () => {
        setShowModal(true)

        if (confirmTrackingEvent) {
          track(confirmTrackingEvent)
        }
      },
    }
  }, [confirmTrackingEvent, isSyncRunning, marketplaceIntegration, canShowSyncAction, isExportMode])

  return {
    dataCollectionSyncAction,
    syncModal: showModal ? <Modals setShowModal={setShowModal} onConfirm={onConfirmSync} /> : null,
  }
}
